import type { CanDoItem } from '../../modules/tutor/api/tutor.client';

/** Rubric scores from lesson can-do eval are 1–4. */
export const CAN_DO_STRONG_THRESHOLD = 2.75;
export const CAN_DO_FOCUS_THRESHOLD = 2.49;

export type CanDoLookup = Map<string, { statementEn: string; statementTa: string }>;

export function buildCanDoLookupFromLessons(
  bundles: Array<{ can_dos: CanDoItem[] }>
): CanDoLookup {
  const map: CanDoLookup = new Map();
  for (const bundle of bundles) {
    for (const cd of bundle.can_dos ?? []) {
      const id = String(cd.id || '').trim();
      if (!id || map.has(id)) continue;
      map.set(id, {
        statementEn: String(cd.statement_en || '').trim(),
        statementTa: String(cd.statement_ta || '').trim(),
      });
    }
  }
  return map;
}

export function canDoIdFromRow(row: Record<string, unknown>): string {
  const id = row._id ?? row.can_do_id;
  return id != null ? String(id).trim() : '';
}

export function rubricScoreNum(row: Record<string, unknown>): number | null {
  const v = row.avg_score;
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

/** Student-facing skill title (English first, Tamil subtitle). */
export function skillDisplayTitle(
  row: Record<string, unknown>,
  lookup: CanDoLookup
): { title: string; subtitle?: string } {
  const id = canDoIdFromRow(row);
  const meta = id ? lookup.get(id) : undefined;
  const en =
    String(row.statement_en || '').trim() ||
    meta?.statementEn ||
    '';
  const ta =
    String(row.statement_ta || '').trim() ||
    meta?.statementTa ||
    '';

  if (en) {
    return { title: en, subtitle: ta || undefined };
  }
  if (ta) {
    return { title: ta };
  }
  if (id) {
    return {
      title: id.replace(/^cd_/i, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    };
  }
  return { title: 'Tamil skill' };
}

export type SkillLevel = 'starting' | 'growing' | 'doing_well' | 'super';

export function skillLevelFromRubric(avg: number | null): SkillLevel {
  if (avg == null) return 'starting';
  if (avg >= 3.5) return 'super';
  if (avg >= 2.75) return 'doing_well';
  if (avg >= 2) return 'growing';
  return 'starting';
}

export const SKILL_LEVEL_UI: Record<
  SkillLevel,
  { label: string; emoji: string; barPct: number; chipClass: string }
> = {
  starting: {
    label: 'Just starting',
    emoji: '🌱',
    barPct: 25,
    chipClass:
      'bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-950/50 dark:text-sky-200 dark:border-sky-800',
  },
  growing: {
    label: 'Growing',
    emoji: '💪',
    barPct: 50,
    chipClass:
      'bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-800',
  },
  doing_well: {
    label: 'Doing well',
    emoji: '⭐',
    barPct: 75,
    chipClass:
      'bg-emerald-100 text-emerald-900 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-100 dark:border-emerald-800',
  },
  super: {
    label: 'Super star',
    emoji: '🌟',
    barPct: 100,
    chipClass:
      'bg-purple-100 text-app-primary border-purple-200 dark:bg-purple-950/40 dark:text-purple-100 dark:border-purple-800',
  },
};

export function lessonScorePercent(raw: unknown): number | null {
  if (raw == null) return null;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, Math.round(n)));
}

export function lessonScoreStars(pct: number | null): number {
  if (pct == null) return 0;
  if (pct >= 75) return 4;
  if (pct >= 50) return 3;
  if (pct >= 25) return 2;
  if (pct > 0) return 1;
  return 0;
}

export function lessonScoreFriendly(pct: number | null): string {
  if (pct == null) return 'Not scored yet';
  if (pct >= 75) return 'Amazing!';
  if (pct >= 50) return 'Good job!';
  if (pct >= 25) return 'Keep going!';
  if (pct > 0) return 'Nice try!';
  return 'Try again';
}

export type FriendlyRecommendationLabels = {
  practiceMorePrefix: string;
  revisitLesson: (lessonNo: string) => string;
  extraPractice: string;
};

/** Turn backend recommendation strings into kid-friendly tips. */
export function friendlyRecommendation(
  raw: string,
  lookup: CanDoLookup,
  labels?: FriendlyRecommendationLabels
): string {
  let text = raw.trim();
  const canDoMatch = text.match(/Can-Do\s+(\S+)/i);
  if (canDoMatch) {
    const id = canDoMatch[1].replace(/\.$/, '');
    const { title } = skillDisplayTitle({ _id: id }, lookup);
    text = text.replace(canDoMatch[0], `“${title}”`);
  }
  text = text.replace(
    /Schedule extra practice focused on/i,
    labels?.practiceMorePrefix ?? 'Practice more:'
  );
  text = text.replace(
    /Revisit lesson (\d+) and complete another tutor session for spaced practice\.?/i,
    (_m, n: string) =>
      labels?.revisitLesson(n) ??
      `Try Lesson ${n} again in Tamil Tutor — one more short session helps!`
  );
  text = text.replace(/spaced practice/gi, labels?.extraPractice ?? 'extra practice');
  text = text.replace(/MongoDB|Mongo/gi, '');
  return text.trim();
}

export function partitionSkills(
  rows: Array<Record<string, unknown>>
): { strengths: Array<Record<string, unknown>>; focus: Array<Record<string, unknown>> } {
  const strengths: Array<Record<string, unknown>> = [];
  const focus: Array<Record<string, unknown>> = [];
  for (const row of rows) {
    const avg = rubricScoreNum(row);
    if (avg != null && avg >= CAN_DO_STRONG_THRESHOLD) {
      strengths.push(row);
    } else {
      focus.push(row);
    }
  }
  strengths.sort((a, b) => (rubricScoreNum(b) ?? 0) - (rubricScoreNum(a) ?? 0));
  focus.sort((a, b) => (rubricScoreNum(a) ?? 99) - (rubricScoreNum(b) ?? 99));
  return { strengths, focus };
}
